// Trusselvurdering og sortering av kontaktlista.
//
// EIERKRAVET, ORDRETT: «det er spesielt små båter (USV) og hurtiggående rib
// fartøyer som er farlige for oss som skal overvåke kysten.»
//
// Det er hele grunnen til at rekkefølgen på lista ikke kan være «nyeste først».
// En USV som nærmer seg i 30 knop skal ligge over et lasteskip som meldte seg
// et halvt sekund senere. Sorteringen er derfor en EKSPLISITT, testet regel og
// ikke et biprodukt av hvilken melding som kom sist over sambandet.
//
// PRINSIPPET: operatøren skal kunne SE hvorfor en kontakt ligger øverst. Derfor
// returnerer `trusselvurdering` ikke bare et tall, men bidragene som skapte det,
// og kontaktlista viser dem. En rangering man ikke kan etterprøve er en
// rangering man slutter å tro på — og da leses lista i vilkårlig rekkefølge
// likevel.
import type { Affiliation } from '@skade/contracts';
import type { Fartoyklasse, Kystkontakt, Operatorflagg } from '../api/typer.js';
import { friskhet, STANDARD_TERSKLER, type Terskler } from '@skade/sensorflate';

export interface Bidrag {
  /** Kort klartekst, vises i lista: «USV», «28 kn», «1,2 km». */
  navn: string;
  /** Poeng dette bidraget la til. Kan være negativt (avskrevet, venn). */
  poeng: number;
}

export interface Trusselvurdering {
  kontaktId: string;
  /** 0–100. Ikke en sannsynlighet — en RANGERINGSVERDI. Se kommentaren under. */
  poeng: number;
  bidrag: Bidrag[];
  /**
   * Setter operatørflagget rekkefølgen direkte? Da skal lista si det, slik at
   * ingen tror maskinen fant på det.
   */
  styrtAvOperator: boolean;
}

/**
 * HVORFOR 0–100 OG IKKE EN SANNSYNLIGHET: vi har ingen kalibrering som gjør et
 * tall om til «40 % sjanse for at dette er en trussel», og å presentere et
 * ukalibrert tall i prosent er å påstå presisjon vi ikke har. Tallet finnes for
 * å RANGERE, og UI-et viser det som en rangeringsverdi med bidragene ved siden.
 */
const MAKS = 100;

/**
 * Tilhørighet veier tyngst, for det er den ene vurderingen et menneske har
 * gjort. `friend` trekker ned: en egen enhet skal ikke ligge øverst på en
 * trussel-liste bare fordi den går fort.
 */
const TILHORIGHET_POENG: Record<Affiliation, number> = {
  hostile: 40,
  unknown: 22,
  neutral: 4,
  friend: -30,
};

/**
 * Klassevektene er eierens prioritering, ikke en generell fartøytabell. USV og
 * rib ligger høyest fordi de er små, raske og vanskelige å se — altså farlige
 * NETTOPP for en post som skal oppdage dem i tid.
 */
const KLASSE_POENG: Record<Fartoyklasse, number> = {
  usv: 26,
  rib: 24,
  smaabaat: 10,
  storfartoy: 6,
  ukjent: 14,
};

/**
 * Operatørflagget er ikke ett bidrag blant flere — det er en overstyring.
 * `trussel` løfter kontakten til topps og `avskrevet` sender den til bunns,
 * uansett hva automatikken mener. Om et menneske har sett på kontakten og
 * bestemt seg, skal maskinen ikke argumentere.
 */
const FLAGG_POENG: Record<Operatorflagg, number> = {
  trussel: MAKS,
  folg: 12,
  ingen: 0,
  avskrevet: -MAKS,
};

export interface TrusselValg {
  /** Postens posisjon — nærhet vurderes mot den. Uten den droppes nærhetsbidraget. */
  post?: { lat: number; lon: number };
  terskler?: Terskler;
}

/**
 * Fart i knop → poeng. Terskelen på 20 kn er der en rib eller USV blir et
 * reaksjonstidsproblem: 20 kn er ~10 m/s, og da spises en kilometer på under to
 * minutter. Under 6 kn gis ingenting — en båt i gangfart er ikke et tempoproblem.
 */
function fartPoeng(fartKn: number | undefined): number {
  if (fartKn === undefined || !Number.isFinite(fartKn) || fartKn < 6) return 0;
  if (fartKn >= 35) return 22;
  if (fartKn >= 20) return 16;
  if (fartKn >= 12) return 8;
  return 4;
}

/**
 * Nærhet i meter → poeng. Poenget er reaksjonstid, ikke avstand i seg selv:
 * under 2 km er en hurtiggående rib inne på posten før operatøren rekker å
 * varsle noen, og det er den situasjonen lista skal fange.
 */
function naerhetPoeng(avstandM: number): number {
  if (avstandM <= 1000) return 18;
  if (avstandM <= 2000) return 13;
  if (avstandM <= 5000) return 7;
  if (avstandM <= 10_000) return 3;
  return 0;
}

/**
 * Hvor mange SENSORARTER ser kontakten. Ikke antall observasjoner — hundre
 * radarekko er fortsatt én sensor som kan ta feil. Uavhengige arter er det som
 * gjør en kontakt til et faktum, og lista skal la en trippelt bekreftet kontakt
 * gå foran en enslig peiling ved ellers likt.
 */
function bekreftelsePoeng(antallArter: number): number {
  if (antallArter >= 3) return 10;
  if (antallArter === 2) return 6;
  return 0;
}

/** Meter mellom to punkter, liten flat tilnærming. Se kommentar i `avstandM`. */
export function avstandM(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  // Ekvirektangulær tilnærming. Innenfor en kystposts rekkevidde (titalls km,
  // ~59–60 °N) er feilen på meternivå, og tallet brukes til å RANGERE — ikke til
  // en målløsning. Målløsninger bruker `polarTarget`/`haversineM` fra
  // @skade/contracts, som er kilden til sannhet for geometri (grunnloven §3).
  const R = 6_371_000;
  const φ = ((a.lat + b.lat) / 2) * (Math.PI / 180);
  const dx = (b.lon - a.lon) * (Math.PI / 180) * Math.cos(φ);
  const dy = (b.lat - a.lat) * (Math.PI / 180);
  return Math.round(R * Math.hypot(dx, dy));
}

/** Antall unike sensorarter bak kontakten, gitt et oppslag sensorId → art. */
export function antallArter(k: Kystkontakt, artFor: (sensorId: string) => string | undefined): number {
  const arter = new Set<string>();
  for (const o of k.observasjoner) {
    const a = artFor(o.sensorId);
    if (a) arter.add(a);
  }
  return arter.size;
}

/**
 * Vurder én kontakt. `naaMs` er med fordi en gammel kontakt skal falle: en
 * peiling fra fem minutter siden er ikke en trussel, den er en hendelse i
 * loggen. Dempingen er MULTIPLIKATIV og ikke et fratrekk, slik at en gammel
 * hostil USV fortsatt ligger over en gammel nøytral pram.
 */
export function trusselvurdering(
  k: Kystkontakt,
  naaMs: number,
  artFor: (sensorId: string) => string | undefined,
  valg: TrusselValg = {},
): Trusselvurdering {
  const bidrag: Bidrag[] = [];

  // Operatørens overstyring først, så den er synlig øverst i begrunnelsen.
  if (k.flagg !== 'ingen') {
    bidrag.push({ navn: k.flagg === 'trussel' ? 'Merket trussel' : k.flagg === 'avskrevet' ? 'Avskrevet' : 'Følges', poeng: FLAGG_POENG[k.flagg] });
  }

  const tp = TILHORIGHET_POENG[k.tilhorighet];
  if (tp !== 0) bidrag.push({ navn: TILHORIGHET_TEKST[k.tilhorighet], poeng: tp });

  const kp = KLASSE_POENG[k.klasse];
  if (kp !== 0) bidrag.push({ navn: KLASSE_TEKST[k.klasse], poeng: kp });

  const fp = fartPoeng(k.fartKn);
  if (fp !== 0) bidrag.push({ navn: `${Math.round(k.fartKn ?? 0)} kn`, poeng: fp });

  if (valg.post && k.lat !== undefined && k.lon !== undefined) {
    const d = avstandM(valg.post, { lat: k.lat, lon: k.lon });
    const np = naerhetPoeng(d);
    if (np !== 0) bidrag.push({ navn: d < 1000 ? `${d} m` : `${(d / 1000).toFixed(1)} km`, poeng: np });
  }

  const arter = antallArter(k, artFor);
  const bp = bekreftelsePoeng(arter);
  if (bp !== 0) bidrag.push({ navn: `${arter} sensorarter`, poeng: bp });

  const raa = bidrag.reduce((s, b) => s + b.poeng, 0);

  // Alderdemping: en «stille» kontakt teller halvt, en «borte» kontakt en
  // femtedel. Den forsvinner ALDRI helt av seg selv — det er operatøren som
  // avskriver kontakter, ikke en tidtaker.
  const t = valg.terskler ?? STANDARD_TERSKLER;
  const f = friskhet(Math.max(0, naaMs - k.sisteTsMs), t);
  const demping = f === 'borte' ? 0.2 : f === 'stille' ? 0.5 : f === 'sen' ? 0.85 : 1;
  if (demping < 1) bidrag.push({ navn: `${FRISKHET_TEKST[f]} (×${demping})`, poeng: Math.round(raa * demping - raa) });

  const poeng = Math.max(0, Math.min(MAKS, Math.round(raa * demping)));
  return { kontaktId: k.id, poeng, bidrag, styrtAvOperator: k.flagg === 'trussel' || k.flagg === 'avskrevet' };
}

const TILHORIGHET_TEKST: Record<Affiliation, string> = {
  hostile: 'Hostil',
  unknown: 'Ukjent tilhørighet',
  neutral: 'Nøytral',
  friend: 'Egen/venn',
};

const KLASSE_TEKST: Record<Fartoyklasse, string> = {
  usv: 'USV',
  rib: 'Rib',
  smaabaat: 'Småbåt',
  storfartoy: 'Større fartøy',
  ukjent: 'Uklassifisert',
};

const FRISKHET_TEKST = { fersk: 'Fersk', sen: 'Sen', stille: 'Stille', borte: 'Borte' } as const;

export interface RangertKontakt {
  kontakt: Kystkontakt;
  vurdering: Trusselvurdering;
}

/**
 * Sorter kontaktlista. Rekkefølgen er:
 *   1. poeng, høyest først
 *   2. ved likhet: nyeste observasjon først (den som skjer NÅ er mest aktuell)
 *   3. ved likhet der også: id, så lista ikke hopper mellom to like kontakter
 *      hver gang den tegnes om. En liste som stokker seg selv hvert sekund er
 *      umulig å peke på — det er en ekte brukbarhetsfeil, ikke pedanteri.
 */
export function sorterPaaTrussel(
  kontakter: readonly Kystkontakt[],
  naaMs: number,
  artFor: (sensorId: string) => string | undefined,
  valg: TrusselValg = {},
): RangertKontakt[] {
  return kontakter
    .map((kontakt) => ({ kontakt, vurdering: trusselvurdering(kontakt, naaMs, artFor, valg) }))
    .sort((a, b) =>
      b.vurdering.poeng - a.vurdering.poeng
      || b.kontakt.sisteTsMs - a.kontakt.sisteTsMs
      || a.kontakt.id.localeCompare(b.kontakt.id, 'no'));
}

/**
 * Grov trussel-bånd for farge og etikett. Tre bånd og ikke fem: operatøren
 * trenger «se på denne nå», «hold øye med den» og «registrert». Flere nivåer
 * gir ikke mer informasjon, bare mer å tolke.
 */
export type Trusselband = 'akutt' | 'folg' | 'registrert';

export function trusselband(poeng: number): Trusselband {
  if (poeng >= 60) return 'akutt';
  if (poeng >= 30) return 'folg';
  return 'registrert';
}

export const TRUSSELBAND_NAVN: Record<Trusselband, string> = {
  akutt: 'Akutt',
  folg: 'Følg',
  registrert: 'Registrert',
};
